import { useLazyLoadQuery } from 'react-relay';
import { useParams } from 'react-router';
import { getComponentRunningMessage } from 'src/products/connector/pages/connectors-landing/utils/getComponentRunningMessage';
import { buildConnectorAllStatuses } from 'src/products/connector/utils/buildConnectorAllStatuses';
import { createConnectorStatusMessage } from 'src/products/connector/utils/createConnectorStatusMessage';
import { printLocalDatetimeWithTimezone } from 'src/products/connector/utils/printLocalDatetimeWithTimezone';
import { ConnectorByIdQuery } from 'src/services/DOM/queries/connectorById';
import { connectorByIdQuery } from 'src/services/DOM/queries/connectorById/__generated__/connectorByIdQuery.graphql';
import { buildCreatedString } from './util/buildCreatedString';
import { buildUpdateScheduleString } from './util/buildUpdateScheduleString';
import { buildSourcesOverview } from './util/buildSourcesOverview';
import { buildModelsOverview } from './util/buildModelsOverview';
import { buildDestinationsOverview } from './util/buildDestinationsOverview';
import { useGetOverviewProdReturn } from './model';
import { useMemo } from 'react';
import { ConnectorStages } from 'src/products/connector/constants';

export const useGetOverviewProd = (
  stage?: ConnectorStages
): useGetOverviewProdReturn => {
  const { connector_name } = useParams<{ connector_name: string }>();

  const { connector } = useLazyLoadQuery<connectorByIdQuery>(
    ConnectorByIdQuery,
    { name: connector_name }
  );

  const {
    name,
    displayName,
    sources,
    models,
    statusObject: prodStatusObject,
    devStatusObject,
    qcStatusObject,
  } = connector;

  let statusObject = prodStatusObject;

  if (stage === ConnectorStages.DEV) {
    statusObject = devStatusObject;
  }

  if (stage === ConnectorStages.QC) {
    statusObject = qcStatusObject;
  }

  const stagesStatuses = useMemo(
    () => ({
      publish: Boolean(prodStatusObject?.status),
      dev: Boolean(devStatusObject?.status),
      qc: Boolean(qcStatusObject?.status),
    }),
    [prodStatusObject, devStatusObject, qcStatusObject]
  );

  const {
    failedConnectorComponent,
    connectorStatusObject,
    connectorStatus,
    failedComponentName,
  } = buildConnectorAllStatuses({
    sources,
    models,
    status: statusObject?.status,
    stage,
  });

  const statusMessage = createConnectorStatusMessage({
    sources,
    failedConnectorComponent,
    connectorEndDateTime: statusObject?.endDatetime,
    connectorStatus,
    simplifiedErrorMessage: true,
  });

  const runningComponentMessage = getComponentRunningMessage({
    sources,
    models,
    statusObject,
  });

  const lastRun = printLocalDatetimeWithTimezone(
    'MMM D YYYY h:mm A',
    statusObject?.endDatetime
  );

  const connectorRunningObject = useMemo(
    () => ({
      isRunning: Boolean(statusObject?.isRunning),
      runningMessage: runningComponentMessage ?? '',
    }),
    [statusObject, runningComponentMessage]
  );

  const data = useMemo(() => {
    const created = buildCreatedString(sources);
    const updateSchedule = buildUpdateScheduleString(sources);
    const mappedSources = buildSourcesOverview(sources);
    const mappedModels = buildModelsOverview(models, stage);
    const mappedDestinations = buildDestinationsOverview(models);

    const connector = {
      name,
      displayName,
      statusMessage,
      connectorRunningObject,
      failedComponentName,
      connectorStatus: connectorStatusObject,
      status: connectorStatus,
      failedConnectorComponent,
      lastRun,
      created,
      updateSchedule,
      sources: mappedSources,
      models: mappedModels,
      destinations: mappedDestinations,
      stage,
    };

    return {
      stagesStatuses,
      connector,
    };
  }, [sources, statusObject, models, connectorRunningObject, stagesStatuses]);

  return { data };
};
